// Entry point of the data layer: fetch and decode public/data into WorldData.
//
//   phase 1  grids + every top-level JSON, in parallel
//   phase 2  files that world.json names: control/<series>.json and geometry/*.geojson
//
// countries.json is required (nothing can be drawn without it). Every other file is optional and
// degrades to empty data with a console warning.

import type {
  Admin1, BorderChange, City, ControlSeries, Country, Indicators, Milestone, PovOverrides, Resources, Source,
  SourceId, Wonder, WorldState,
} from '../core/schema.ts';
import { eventLoader } from './events.ts';
import { loadFlags } from './flags.ts';
import { makeReader, type Reader } from './fetch.ts';
import { GRID_FILES, loadGrid } from './grid.ts';
import { loadLakeGroups } from './lakes.ts';
import { loadSeaIce } from './seaice.ts';
import type { CuratedWorld, EventIndex, GeoJson, LoadOptions, LoadProgress, UcdpData, WorldData } from './types.ts';

const COUNTRIES = 'countries.json';
/** 'YYYY-MM' → 'YYYY'. */
const YEAR_CHARS = 4;

function emptyWorld(): CuratedWorld {
  return {
    as_of: '', actors: [], blocs: [], memberships: [], treaties: [], sanctions: [], wars: [], control: [],
    leaders: [], perspectives: [], statements: [], unverified: [], cities: [],
  };
}

/** world.json with every array present, whatever the validator left out. */
function normaliseWorld(w: Partial<CuratedWorld> | null): CuratedWorld {
  return { ...emptyWorld(), ...(w ?? {}) };
}

/** sources.json is a map { id: Source }; an array of Source is accepted too. */
function sourceMap(raw: Record<SourceId, Source> | Source[] | null): Map<SourceId, Source> {
  if (!raw) {
    return new Map();
  }
  const list = Array.isArray(raw) ? raw : Object.values(raw);
  return new Map(list.map((s) => [s.id, s]));
}

/** Counts finished files; `total` may grow while files are queued (it is read on each tick). */
function progressTicker(onProgress?: (p: LoadProgress) => void) {
  const t = { total: 0, done: 0 };
  return {
    add(n: number) {
      t.total += n;
    },
    tick(label: string) {
      t.done++;
      onProgress?.({ done: t.done, total: t.total, label });
    },
  };
}

type Tick = (label: string) => void;

/** Fetch `path`, then tick progress with its name. */
function tracked<T>(p: Promise<T>, path: string, tick: Tick): Promise<T> {
  return p.then((v) => {
    tick(path);
    return v;
  });
}

function seriesIds(world: WorldState): string[] {
  const ids = new Set<string>();
  for (const e of world.control) {
    if ('series' in e.geometry) {
      ids.add(e.geometry.series);
    }
  }
  return [...ids];
}

function geometryFiles(world: WorldState): string[] {
  const files = new Set<string>();
  for (const e of world.control) {
    if ('file' in e.geometry) {
      files.add(e.geometry.file);
    }
  }
  return [...files];
}

async function loadNamed<T>(reader: Reader, paths: string[], tick: Tick): Promise<Map<string, T>> {
  const out = new Map<string, T>();
  await Promise.all(paths.map(async (path) => {
    const v = await tracked(reader.json<T>(path), path, tick);
    if (v) {
      out.set(path, v);
    }
  }));
  return out;
}

async function loadControl(reader: Reader, paths: string[], tick: Tick): Promise<Map<string, ControlSeries>> {
  const byPath = await loadNamed<ControlSeries>(reader, paths, tick);
  const out = new Map<string, ControlSeries>();
  for (const s of byPath.values()) {
    out.set(s.id, s);
  }
  return out;
}

/** The years the events index lists: their files must load. */
function bakedYears(index: EventIndex): Set<number> {
  return new Set(index.months.map(([month]) => Number(month.slice(0, YEAR_CHARS))));
}

/** Fetch and decode everything under `baseUrl` (e.g. '/data'). */
export async function loadWorldData(baseUrl: string, opts: LoadOptions = {}): Promise<WorldData> {
  const reader = makeReader(baseUrl, opts.fetch);
  const progress = progressTicker(opts.onProgress);
  const tick: Tick = (label) => progress.tick(label);
  const j = <T>(path: string) => tracked(reader.json<T>(path), path, tick);

  // Phase 1. Every promise is created before any can resolve, so the total is set in time.
  const files = {
    countries: j<Country[]>(COUNTRIES),
    admin1: j<Admin1[]>('admin1.json'),
    cities: j<City[]>('cities.json'),
    wonders: j<Wonder[]>('wonders.json'),
    resources: j<Resources>('resources.json'),
    pov: j<PovOverrides>('pov.json'),
    borders: j<BorderChange[]>('borders-history.json'),
    indicators: j<Indicators>('indicators.json'),
    milestones: j<Milestone[]>('milestones.json'),
    world: j<Partial<CuratedWorld>>('world.json'),
    sources: j<Record<SourceId, Source> | Source[]>('sources.json'),
    ucdp: j<UcdpData>('conflicts-ucdp.json'),
    eventIndex: j<EventIndex>('events-index.json'),
  };
  const gridP = tracked(loadGrid(reader), GRID_FILES.join(', '), tick);
  const iceP = tracked(loadSeaIce(reader), 'seaice.bin', tick);
  const lakesP = tracked(loadLakeGroups(reader), 'lake-hexes.json', tick);
  const flagsP = tracked(loadFlags(baseUrl, opts.fetch, reader), 'flags/index.json', tick);
  progress.add(Object.keys(files).length + 4);

  const countries = await files.countries;
  if (!countries) {
    throw new Error(`data: ${COUNTRIES} is required (${reader.warnings.join('; ')})`);
  }

  // Phase 2: files named by the curated control episodes.
  const ws = normaliseWorld(await files.world);
  const series = seriesIds(ws).map((id) => `control/${id}.json`);
  const shapes = geometryFiles(ws);
  progress.add(series.length + shapes.length);
  const [control, geometry] = await Promise.all([
    loadControl(reader, series, tick),
    loadNamed<GeoJson>(reader, shapes, tick),
  ]);

  const pov = await files.pov;
  const eventIndex = (await files.eventIndex) ?? { months: [], sources: [] };
  const seaIce = await iceP;
  const lakeGroups = await lakesP;
  const flags = await flagsP;

  // The flag manifest carries the source of its own dates; the baked sources.json wins on a clash.
  const sources = sourceMap(await files.sources);
  for (const s of flags?.sources ?? []) {
    if (!sources.has(s.id)) {
      sources.set(s.id, s);
    }
  }
  return {
    grid: await gridP,
    countries,
    admin1: (await files.admin1) ?? [],
    cities: (await files.cities) ?? [],
    wonders: (await files.wonders) ?? [],
    resources: (await files.resources) ?? { deposits: [], stockpiles: {} },
    pov: { usa: [], chn: [], rus: [], ...(pov ?? {}) },
    borders: (await files.borders) ?? [],
    indicators: (await files.indicators) ?? { meta: {}, series: {} },
    milestones: (await files.milestones) ?? [],
    world: ws,
    sources,
    ucdp: (await files.ucdp) ?? { conflicts: [] },
    control,
    geometry,
    ...(seaIce ? { seaIce } : {}),
    ...(lakeGroups ? { lakeGroups } : {}),
    ...(flags ? { flags } : {}),
    eventIndex,
    loadEvents: eventLoader(reader, bakedYears(eventIndex)),
    warnings: reader.warnings,
  };
}
